import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  HashRouter as Router,
  useLocation,
} from "react-router-dom";
import AppRoutes from "./routes";
import Headermain from "../components/header";
import AnimatedCursor from "../hooks/AnimatedCursor";
import ErrorBoundary from "../components/ErrorBoundary";
import "./App.css";
import WhatsAppIcon from '../components/header/contact';

// ScrollToTop Component
function ScrollToTop({ children }) {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const sectionId = hash.replace("#", "");
    let intervalId;
    let attempts = 0;

    // Poll until the section element exists in the DOM.
    // Delay start by 450ms so the page-enter CSS transition (400ms) finishes first.
    const startId = setTimeout(() => {
      intervalId = setInterval(() => {
        const section = document.getElementById(sectionId);
        attempts += 1;

        if (section) {
          clearInterval(intervalId);
          const headerOffset = 70;
          const top = section.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: "smooth" });
        } else if (attempts >= 20) {
          clearInterval(intervalId);
        }
      }, 50);
    }, 450);

    return () => {
      clearTimeout(startId);
      clearInterval(intervalId);
    };
  }, [pathname, hash]);

  return children;
}

// Back-to-top button
function BackToTop() {
  const [visible, setVisible] = useState(false);
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute("data-theme") || "light"
  );

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute("data-theme") || "light");
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      className="back-to-top"
      style={{
        position: "fixed",
        bottom: "80px",
        right: "20px",
        zIndex: 1050,
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        border:
          theme === "dark"
            ? "1px solid rgba(255,255,255,0.2)"
            : "1px solid rgba(17,24,39,0.2)",
        background: "rgba(13,110,253,0.85)",
        color: theme === "dark" ? "#fff" : "#f8fafc",
        fontSize: "18px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(6px)",
        boxShadow: "0 4px 16px rgba(13,110,253,0.4)",
        transition: "opacity 0.3s",
      }}
    >
      ↑
    </button>
  );
}

// Main App Component
export default function App() {
  const [loading, setLoading] = useState(true);
  const text = "Crafting Intelligence";

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-text">
          {text.split("").map((letter, index) => (
            <span key={index} style={{ animationDelay: `${index * 0.2}s` }}>
              {letter}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <div className="cursor__dot">
          <AnimatedCursor
            innerSize={0}
            outerSize={34}
            color="56, 189, 248"
            outerAlpha={0.12}
            innerScale={1}
            outerScale={1.08}
            trailingSpeed={14}
            hideNativeCursor={false}
            innerStyle={{
              display: "none",
            }}
            outerStyle={{
              background: "radial-gradient(circle, rgba(56,189,248,0.25) 0%, rgba(56,189,248,0.16) 35%, rgba(56,189,248,0.07) 60%, rgba(56,189,248,0) 72%)",
              border: "0",
              filter: "blur(0.5px)",
            }}
          />
        </div>
        <ScrollToTop>
          <Headermain />
          <AppRoutes />
          <WhatsAppIcon/>
          <BackToTop />
        </ScrollToTop>
      </Router>
    </ErrorBoundary>
  );
}
